import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDiscord } from '@fortawesome/free-brands-svg-icons';
import { 
  faSignOutAlt, faGlobe, faLock, 
  faHeart, faThumbsUp, faSadTear, faAngry, faLaugh
} from '@fortawesome/free-solid-svg-icons';
import { theme } from '../styles/GlobalStyles';
import { supabase, signInWithDiscord } from '../utils/supabaseClient';

const AdminContainer = styled.div`
  max-width: 1200px;
  margin: 60px auto;
  padding: 0 20px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const PageTitle = styled.h1`
  font-size: 2.8rem;
  margin-bottom: 15px;
  background: linear-gradient(to right, ${theme.colors.primary}, ${theme.colors.accent});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const PageDescription = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto;
`;

const LoginContainer = styled.div`
  text-align: center;
  padding: 60px 0;
`;

const LoginButton = styled.button`
  background-color: #5865F2;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 15px 30px;
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 auto;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #4752c4;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(88, 101, 242, 0.3);
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: rgba(255, 255, 255, 0.05);
  padding: 15px 25px;
  border-radius: 10px;
  margin-bottom: 30px;
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const Avatar = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 2px solid ${theme.colors.primary};
`;

const UserName = styled.div`
  h3 {
    margin: 0;
    font-size: 1.2rem;
  }
  
  p {
    margin: 5px 0 0;
    color: ${theme.colors.textSecondary};
    font-size: 0.9rem;
  }
`;

const LogoutButton = styled.button`
  background-color: transparent;
  color: ${theme.colors.textSecondary};
  border: 1px solid ${theme.colors.textSecondary};
  border-radius: 6px;
  padding: 8px 15px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: ${theme.colors.primary};
    border-color: ${theme.colors.primary};
    background-color: rgba(255, 107, 107, 0.05);
  }
`;

const ErrorMessage = styled.div`
  background-color: rgba(246, 71, 71, 0.1);
  border-left: 4px solid #f64747;
  padding: 15px 20px;
  margin: 20px 0;
  border-radius: 0 8px 8px 0;
  color: #f64747;
  font-size: 0.95rem;
  
  strong {
    display: block;
    margin-bottom: 5px;
  }
`;

const GlobalSecretsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 20px;
`;

const SecretItem = styled.div`
  background-color: ${theme.colors.backgroundAlt};
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
  }
`;

const SecretContent = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 20px;
  color: ${theme.colors.secondary};
  white-space: pre-wrap;
  word-break: break-word;
`;

const SecretMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
  color: ${theme.colors.textSecondary};
`;

const SecretDate = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const NSFWTag = styled.span`
  background-color: ${theme.colors.accent};
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
`;

const ReactionBar = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 15px;
`;

const ReactionButton = styled.button`
  background-color: ${props => props.active ? theme.colors.primary + '30' : 'transparent'};
  color: ${props => props.active ? theme.colors.primary : theme.colors.textSecondary};
  border: 1px solid ${props => props.active ? theme.colors.primary : theme.colors.border};
  border-radius: 20px;
  padding: 5px 12px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${theme.colors.primary}20;
    color: ${theme.colors.primary};
  }
  
  span {
    font-weight: ${props => props.active ? '600' : 'normal'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 30px;
  color: ${theme.colors.textSecondary};
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
`;

const LoadMoreButton = styled.button`
  background-color: transparent;
  border: 2px solid ${theme.colors.primary};
  color: ${theme.colors.primary};
  border-radius: 50px;
  padding: 10px 25px;
  font-size: 1rem;
  margin: 30px auto;
  display: block;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${theme.colors.primary}10;
    transform: translateY(-2px);
  }
  
  &:disabled {
    border-color: #cccccc;
    color: #999999;
    cursor: not-allowed;
    transform: none;
  }
`;

// Definir los emojis disponibles para reacciones
const AVAILABLE_REACTIONS = [
  { emoji: '❤️', icon: faHeart, name: 'heart' },
  { emoji: '👍', icon: faThumbsUp, name: 'thumbsup' },
  { emoji: '😂', icon: faLaugh, name: 'laugh' },
  { emoji: '😢', icon: faSadTear, name: 'sad' },
  { emoji: '😡', icon: faAngry, name: 'angry' }
];

const Admin = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [secrets, setSecrets] = useState([]);
  const [userReactions, setUserReactions] = useState({});
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    checkUser();
  }, []);

  // Verificar si hay un usuario autenticado
  const checkUser = async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Error al obtener sesión:', sessionError);
        setError(sessionError.message);
        setLoading(false);
        return;
      }
      
      if (session) {
        setUser(session.user);
      } else {
        setUser(null);
      }
      
      // Cargar secretos independientemente de si hay usuario o no
      fetchSecrets();
    } catch (err) {
      console.error('Error inesperado al verificar sesión:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Función para obtener los secretos
  const fetchSecrets = async (loadMore = false) => {
    try {
      setLoading(true);
      const currentPage = loadMore ? page : 0;
      const pageSize = 10;
      
      const { data, error } = await supabase
        .from('secrets')
        .select('*')
        .eq('isglobal', true)
        .order('fecha', { ascending: false })
        .range(currentPage * pageSize, (currentPage + 1) * pageSize - 1);
      
      if (error) throw error;
      
      if (data.length < pageSize) {
        setHasMore(false);
      }
      
      if (loadMore) {
        setSecrets([...secrets, ...data]);
        setPage(currentPage + 1);
      } else {
        setSecrets(data);
        setPage(1);
      }
      
      // Si hay un usuario, cargar sus reacciones
      if (user) {
        fetchUserReactions(data.map(secret => secret.id));
      }
    } catch (error) {
      console.error('Error al obtener secretos:', error);
      setError('No se pudieron cargar los secretos. Por favor, intenta de nuevo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener las reacciones del usuario actual
  const fetchUserReactions = async (secretIds) => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('secret_reactions')
        .select('*')
        .eq('userid', user.id)
        .in('secretid', secretIds);
      
      if (error) throw error;
      
      const reactions = {};
      data.forEach(reaction => {
        reactions[reaction.secretid] = reactions[reaction.secretid] || [];
        reactions[reaction.secretid].push(reaction.emoji);
      });
      
      setUserReactions(reactions);
    } catch (error) {
      console.error('Error al obtener reacciones del usuario:', error);
    }
  };

  // Función para manejar las reacciones a los secretos
  const handleReaction = async (secretId, emoji) => {
    if (!user) {
      setError('Debes iniciar sesión para reaccionar a los secretos.');
      return;
    }
    
    try {
      // Verificar si el usuario ya ha reaccionado con este emoji
      const userReactionsForSecret = userReactions[secretId] || [];
      const hasReacted = userReactionsForSecret.includes(emoji);
      
      if (hasReacted) {
        // Eliminar la reacción
        const { error } = await supabase
          .from('secret_reactions')
          .delete()
          .eq('secretid', secretId)
          .eq('userid', user.id)
          .eq('emoji', emoji);
        
        if (error) throw error;
        
        // Actualizar el estado local
        setUserReactions(prev => {
          const updated = { ...prev };
          updated[secretId] = updated[secretId].filter(e => e !== emoji);
          return updated;
        });
        
        // Actualizar el contador de reacciones en el secreto
        setSecrets(prev => 
          prev.map(secret => 
            secret.id === secretId 
              ? { ...secret, reaction_count: Math.max(0, secret.reaction_count - 1) } 
              : secret
          )
        );
      } else {
        // Añadir la reacción
        const { error } = await supabase
          .from('secret_reactions')
          .insert([
            {
              secretid: secretId,
              userid: user.id,
              emoji: emoji,
              fecha: new Date().toISOString()
            }
          ]);
        
        if (error) throw error;
        
        // Actualizar el estado local
        setUserReactions(prev => {
          const updated = { ...prev };
          updated[secretId] = [...(updated[secretId] || []), emoji];
          return updated;
        });
        
        // Actualizar el contador de reacciones en el secreto
        setSecrets(prev => 
          prev.map(secret => 
            secret.id === secretId 
              ? { ...secret, reaction_count: secret.reaction_count + 1 } 
              : secret
          )
        );
      }
    } catch (error) {
      console.error('Error al gestionar la reacción:', error);
      setError('No se pudo procesar tu reacción. Por favor, intenta de nuevo más tarde.');
    }
  };

  // Función para formatear la fecha
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      await signInWithDiscord();
    } catch (err) {
      console.error('Error al iniciar sesión:', err);
      setError(err.message || 'Error inesperado al iniciar sesión');
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const { error: logoutError } = await supabase.auth.signOut();
      
      if (logoutError) {
        console.error('Error al cerrar sesión:', logoutError);
        setError(logoutError.message);
        return;
      }
      
      setUser(null);
    } catch (err) {
      console.error('Error inesperado al cerrar sesión:', err);
      setError(err.message);
    }
  };

  const loadMoreSecrets = () => {
    fetchSecrets(true);
  };

  if (loading && page === 0) {
    return (
      <AdminContainer>
        <PageHeader>
          <PageTitle>Cargando...</PageTitle>
        </PageHeader>
        <LoadingSpinner>Cargando secretos...</LoadingSpinner>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <PageHeader>
        <PageTitle>Mis Secretos</PageTitle>
        <PageDescription>
          Aquí puedes ver todos tus secretos y los secretos globales.
        </PageDescription>
      </PageHeader>
      
      {error && (
        <ErrorMessage>
          <strong>Error:</strong>
          {error}
        </ErrorMessage>
      )}
      
      {user ? (
      <UserInfo>
        <UserProfile>
          <Avatar 
            src={user.user_metadata?.avatar_url || 'https://cdn.discordapp.com/embed/avatars/0.png'} 
            alt="Avatar" 
          />
          <UserName>
            <h3>{user.user_metadata?.full_name || user.user_metadata?.name || 'Usuario de Discord'}</h3>
            <p>{user.email}</p>
          </UserName>
        </UserProfile>
        
        <LogoutButton onClick={handleLogout}>
          <FontAwesomeIcon icon={faSignOutAlt} /> Cerrar sesión
        </LogoutButton>
      </UserInfo>
      ) : (
        <LoginContainer>
          <LoginButton onClick={handleLogin}>
            <FontAwesomeIcon icon={faDiscord} /> Iniciar sesión con Discord
          </LoginButton>
        </LoginContainer>
      )}
      
      <GlobalSecretsList>
        {secrets.length > 0 ? (
          secrets.map((secret) => (
            <SecretItem key={secret.id}>
              <SecretContent>{secret.contenido}</SecretContent>
              
              <SecretMeta>
                <SecretDate>
                  {formatDate(secret.fecha)}
                </SecretDate>
                {secret.nsfw && (
                  <NSFWTag>NSFW</NSFWTag>
                )}
              </SecretMeta>
              
              <ReactionBar>
                {AVAILABLE_REACTIONS.map((reaction) => {
                  const hasReacted = userReactions[secret.id]?.includes(reaction.emoji);
                  
                  return (
                    <ReactionButton
                      key={reaction.name}
                      onClick={() => handleReaction(secret.id, reaction.emoji)}
                      active={hasReacted}
                    >
                      {reaction.emoji} <span>{reaction.name}</span>
                    </ReactionButton>
                  );
                })}
              </ReactionBar>
            </SecretItem>
          ))
        ) : (
          <EmptyState>
            No hay secretos disponibles.
          </EmptyState>
        )}
      </GlobalSecretsList>
      
      {hasMore && (
        <LoadMoreButton 
          onClick={loadMoreSecrets} 
          disabled={loading}
        >
          {loading ? 'Cargando...' : 'Cargar más secretos'}
        </LoadMoreButton>
      )}
    </AdminContainer>
  );
};

export default Admin; 